import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canManageUsers, canViewAudit, canManageDevices } from "@/config/roles";
import { PageHeader } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import type { Role } from "@/services/types";

interface SettingsCardDef {
  key: string;
  title: string;
  description: string;
  icon: string;
  iconClass: string;
  path: string;
  visible: (role: Role) => boolean;
}

const SETTINGS_CARDS: SettingsCardDef[] = [
  {
    key: "business",
    title: "Business profile",
    description: "The organizer's identity printed on every receipt, voucher and report, with a live header preview.",
    icon: "ri-store-2-line",
    iconClass: "bg-primary-100 text-primary-700",
    path: "/settings/business",
    visible: (role) => role !== "viewer",
  },
  {
    key: "users",
    title: "User management",
    description: "The staff who use this system — create, edit, deactivate and reset passwords. Super Admin only.",
    icon: "ri-user-settings-line",
    iconClass: "bg-accent-100 text-accent-700",
    path: "/settings/users",
    visible: (role) => canManageUsers(role),
  },
  {
    key: "devices",
    title: "IP approvals",
    description: "Authorize staff IP addresses and system access. Maximum 1 active IP per user. Admin & Super Admin.",
    icon: "ri-router-line",
    iconClass: "bg-info-100 text-info-700",
    path: "/settings/devices",
    visible: (role) => canManageDevices(role),
  },
  {
    key: "permissions",
    title: "Role & permission matrix",
    description: "A read-only reference of what each role can view, create, edit, approve and delete across every module.",
    icon: "ri-shield-check-line",
    iconClass: "bg-success-100 text-success-700",
    path: "/settings/permissions",
    visible: () => true,
  },
  {
    key: "numbering",
    title: "Document numbering",
    description: "Prefixes and sequences for member codes, group codes, receipts and vouchers. Forward-only.",
    icon: "ri-hashtag",
    iconClass: "bg-warning-100 text-warning-700",
    path: "/settings/numbering",
    visible: (role) => canManageUsers(role),
  },
  // {
  //   key: "defaults",
  //   title: "Group defaults",
  //   description: "Values pre-filled when a new group is created. A group's own configuration always wins.",
  //   icon: "ri-file-settings-line",
  //   iconClass: "bg-info-100 text-info-700",
  //   path: "/settings/defaults",
  //   visible: (role) => role === "super_admin" || role === "admin",
  // },
  {
    key: "audit",
    title: "Audit log",
    description: "A read-only record of every significant action, with before-and-after values. Admin & Super Admin.",
    icon: "ri-history-line",
    iconClass: "bg-danger-100 text-danger-700",
    path: "/settings/audit",
    visible: (role) => canViewAudit(role),
  },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const cards = SETTINGS_CARDS.filter((c) => c.visible(role));

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Organization configuration, staff users and the system audit trail."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.key} className="cursor-pointer transition-colors hover:border-primary-300 hover:bg-background-100/50">
            <button type="button" onClick={() => navigate(c.path)} className="flex w-full cursor-pointer items-start gap-3 text-left">
              <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-md ${c.iconClass}`}>
                <i className={`${c.icon} text-lg`} />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900">{c.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{c.description}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                  Open <i className="ri-arrow-right-line" />
                </span>
              </div>
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}